/**
 * IndexedDB Database Layer for InspectaOne
 *
 * Provides offline-first data persistence using Dexie.js
 * Stores jobs, inspections, photos, and sync queue data
 *
 * @module lib/db
 */

import Dexie, { Table } from 'dexie';

// ============================================================================
// Type Definitions
// ============================================================================

export interface Job {
  id: string; // UUID from backend
  job_number: string;
  tenant_id: string;
  assigned_to: string; // User ID
  customer: {
    name: string;
    email: string;
    phone: string;
  };
  vehicle: {
    make: string;
    model: string;
    year: number;
    vin?: string;
    registration_number: string;
  };
  scheduled_date: string; // ISO date
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  notes?: string;
  location?: {
    lat: number;
    lng: number;
    address: string;
  };
  created_at: string; // ISO date
  updated_at: string; // ISO date
  synced_at: string | null; // ISO date - last sync to server
  locally_modified: boolean; // Has local changes not synced
}

export interface Inspection {
  id: string; // UUID - generated locally if offline
  job_id: string; // Reference to Job
  status: 'draft' | 'in_progress' | 'completed' | 'submitted';
  sections: InspectionSection[];
  damage_detections?: DamageDetection[];
  inspector: {
    id: string;
    name: string;
    signature?: string; // Base64 or URL
  };
  started_at?: string; // ISO date
  completed_at?: string; // ISO date
  submitted_at?: string; // ISO date
  created_at: string; // ISO date
  updated_at: string; // ISO date
  synced_at: string | null; // ISO date
  locally_modified: boolean;
}

export interface InspectionSection {
  id: string;
  name: string; // 'exterior_front', 'interior', etc.
  display_name: string;
  required: boolean;
  photos: string[]; // Array of photo IDs
  notes?: string;
  completed: boolean;
}

export interface DamageDetection {
  id: string;
  photo_id: string;
  severity: 'minor' | 'moderate' | 'severe';
  type: string; // 'scratch', 'dent', 'rust', etc.
  confidence: number; // 0-1
  bounding_box?: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
}

export interface Photo {
  id: string; // UUID - generated locally
  inspection_id: string; // Reference to Inspection
  section_id: string; // Reference to InspectionSection
  photo_type: string; // section name
  file_name: string;
  mime_type: string;
  size: number; // bytes
  width: number;
  height: number;
  orientation?: number | undefined; // EXIF orientation
  blob: Blob; // Actual photo data
  captured_at: string; // ISO date
  location?: {
    lat: number;
    lng: number;
  } | undefined;
  cloud_path?: string | undefined; // MinIO path after upload
  thumbnail_path?: string | undefined;
  uploaded: boolean;
  upload_attempts: number;
  uploaded_at?: string | undefined; // ISO date
  quality_score: number; // 0-100
  metadata: Record<string, unknown>; // EXIF and other metadata
  created_at: string; // ISO date
}

export interface SyncQueueItem {
  id: string; // UUID
  entity_type: 'job' | 'inspection' | 'photo';
  entity_id: string; // ID of the entity to sync
  action: 'create' | 'update' | 'delete';
  priority: number; // 1-10, higher = more important
  data?: Record<string, unknown>; // Full entity data for create/update
  payload?: Record<string, unknown>; // Alias for data used in some contexts
  retry_count: number;
  max_retries: number;
  last_attempt?: string | undefined; // ISO date
  next_retry_at?: string | undefined; // ISO date for exponential backoff
  error?: string | undefined;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  created_at: string; // ISO date
}

export interface AppMetadata {
  key: string; // Unique key
  value: unknown; // JSON-serializable value
  updated_at: string; // ISO date
}

// ============================================================================
// Database Class
// ============================================================================

export class InspectaOneDB extends Dexie {
  // Tables
  jobs!: Table<Job, string>;
  inspections!: Table<Inspection, string>;
  photos!: Table<Photo, string>;
  sync_queue!: Table<SyncQueueItem, string>;
  metadata!: Table<AppMetadata, string>;

  constructor() {
    super('inspectaone-db');

    // Define schema
    this.version(2).stores({
      // Jobs table with compound indexes
      jobs: 'id, tenant_id, assigned_to, status, scheduled_date, priority, [assigned_to+status], [status+scheduled_date], locally_modified, synced_at',

      // Inspections table with compound indexes
      inspections: 'id, job_id, status, [job_id+status], locally_modified, synced_at, created_at, completed_at',

      // Photos table with compound indexes
      photos: 'id, inspection_id, section_id, [inspection_id+section_id], uploaded, upload_attempts, captured_at',

      // Sync queue with compound indexes
      sync_queue: 'id, [entity_type+entity_id], status, priority, [status+priority], created_at',

      // Metadata table
      metadata: 'key, updated_at',
    });
  }

  /**
   * Initialize the database with default metadata
   */
  async initialize(): Promise<void> {
    const initialized = await this.metadata.get('db_initialized');

    if (!initialized) {
      await this.metadata.put({
        key: 'db_initialized',
        value: true,
        updated_at: new Date().toISOString(),
      });

      await this.metadata.put({
        key: 'db_version',
        value: '2.0.0',
        updated_at: new Date().toISOString(),
      });

      await this.metadata.put({
        key: 'last_sync',
        value: null,
        updated_at: new Date().toISOString(),
      });

      console.log('[InspectaOneDB] Database initialized');
    }
  }

  /**
   * Clear all data (for testing/debugging)
   */
  async clearAll(): Promise<void> {
    await this.jobs.clear();
    await this.inspections.clear();
    await this.photos.clear();
    await this.sync_queue.clear();
    // Keep metadata
  }

  /**
   * Get database statistics
   */
  async getStats() {
    const [jobCount, inspectionCount, photoCount, syncQueueCount] = await Promise.all([
      this.jobs.count(),
      this.inspections.count(),
      this.photos.count(),
      this.sync_queue.count(),
    ]);

    return {
      jobs: jobCount,
      inspections: inspectionCount,
      photos: photoCount,
      syncQueue: syncQueueCount,
      total: jobCount + inspectionCount + photoCount + syncQueueCount,
    };
  }
}

// ============================================================================
// Database Instance
// ============================================================================

export const db = new InspectaOneDB();

// Initialize on import
if (typeof window !== 'undefined') {
  db.initialize().catch(console.error);
}

// ============================================================================
// Legacy API - Backward compatibility with existing idb code
// ============================================================================

export async function getDB() {
  return db;
}

export async function saveJobToCache(job: Job) {
  await db.jobs.put({
    ...job,
    synced_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    locally_modified: false,
  });
}

export async function getJobsFromCache(filters?: { status?: string; tenantId?: string }) {
  if (filters?.status) {
    return await db.jobs.where('status').equals(filters.status).toArray();
  }

  if (filters?.tenantId) {
    return await db.jobs.where('tenant_id').equals(filters.tenantId).toArray();
  }

  return await db.jobs.toArray();
}

export async function savePhotoToCache(photo: {
  id: string;
  inspection_id: string;
  photo_type: string;
  blob: Blob;
  quality_score: number;
  metadata: Record<string, unknown>;
}) {
  await db.photos.put({
    ...photo,
    section_id: photo.photo_type,
    file_name: `${photo.id}.jpg`,
    mime_type: 'image/jpeg',
    size: photo.blob.size,
    width: 0, // Will be set when image loads
    height: 0,
    uploaded: false,
    upload_attempts: 0,
    captured_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
  });
}

export async function getPhotosForInspection(inspectionId: string) {
  return await db.photos.where('inspection_id').equals(inspectionId).toArray();
}

export async function getUnuploadedPhotos() {
  return await db.photos.filter(p => !p.uploaded).toArray();
}

export async function markPhotoAsUploaded(photoId: string) {
  await db.photos.update(photoId, {
    uploaded: true,
    uploaded_at: new Date().toISOString(),
  });
}

export async function addToSyncQueue(
  entityType: string,
  entityId: string,
  action: string,
  data: Record<string, unknown>
) {
  const id = `${entityType}-${entityId}-${Date.now()}`;

  await db.sync_queue.put({
    id,
    entity_type: entityType as 'job' | 'inspection' | 'photo',
    entity_id: entityId,
    action: action as 'create' | 'update' | 'delete',
    data,
    retry_count: 0,
    max_retries: 3,
    priority: 5,
    status: 'pending',
    created_at: new Date().toISOString(),
  });
}

export async function getSyncQueue() {
  return await db.sync_queue.toArray();
}

export async function removeSyncQueueItem(id: string) {
  await db.sync_queue.delete(id);
}

export async function clearAllCaches() {
  await db.clearAll();
}

// ============================================================================
// Enhanced CRUD Operations - Jobs
// ============================================================================

export const jobOperations = {
  /**
   * Get all jobs for a user
   */
  async getAllForUser(userId: string): Promise<Job[]> {
    return await db.jobs
      .where('assigned_to')
      .equals(userId)
      .reverse()
      .sortBy('scheduled_date');
  },

  /**
   * Get job by ID
   */
  async getById(id: string): Promise<Job | undefined> {
    return await db.jobs.get(id);
  },

  /**
   * Get jobs by status
   */
  async getByStatus(userId: string, status: Job['status']): Promise<Job[]> {
    return await db.jobs
      .where(['assigned_to', 'status'])
      .equals([userId, status])
      .reverse()
      .sortBy('scheduled_date');
  },

  /**
   * Get jobs by priority
   */
  async getByPriority(priority: Job['priority']): Promise<Job[]> {
    return await db.jobs.where('priority').equals(priority).toArray();
  },

  /**
   * Create or update job
   */
  async upsert(job: Job): Promise<string> {
    await db.jobs.put(job);
    return job.id;
  },

  /**
   * Bulk upsert jobs (for sync)
   */
  async bulkUpsert(jobs: Job[]): Promise<void> {
    await db.jobs.bulkPut(jobs);
  },

  /**
   * Update job fields
   */
  async update(id: string, updates: Partial<Job>): Promise<void> {
    await db.jobs.update(id, {
      ...updates,
      updated_at: new Date().toISOString(),
      locally_modified: true,
    });
  },

  /**
   * Mark job as locally modified
   */
  async markModified(id: string): Promise<void> {
    await db.jobs.update(id, {
      locally_modified: true,
      updated_at: new Date().toISOString(),
    });
  },

  /**
   * Mark job as synced
   */
  async markSynced(id: string): Promise<void> {
    await db.jobs.update(id, {
      locally_modified: false,
      synced_at: new Date().toISOString(),
    });
  },

  /**
   * Get all locally modified jobs
   */
  async getModified(): Promise<Job[]> {
    return await db.jobs.filter(j => j.locally_modified).toArray();
  },

  /**
   * Delete job
   */
  async delete(id: string): Promise<void> {
    await db.jobs.delete(id);
  },

  /**
   * Search jobs by text
   */
  async search(query: string): Promise<Job[]> {
    const lowerQuery = query.toLowerCase();
    const allJobs = await db.jobs.toArray();
    return allJobs.filter(
      job =>
        job.job_number.toLowerCase().includes(lowerQuery) ||
        job.customer.name.toLowerCase().includes(lowerQuery) ||
        job.vehicle.registration_number.toLowerCase().includes(lowerQuery)
    );
  },
};

// ============================================================================
// Enhanced CRUD Operations - Inspections
// ============================================================================

export const inspectionOperations = {
  /**
   * Get inspection by ID
   */
  async getById(id: string): Promise<Inspection | undefined> {
    return await db.inspections.get(id);
  },

  /**
   * Get inspection by job ID
   */
  async getByJobId(jobId: string): Promise<Inspection | undefined> {
    return await db.inspections.where('job_id').equals(jobId).first();
  },

  /**
   * Get all inspections by status
   */
  async getByStatus(status: Inspection['status']): Promise<Inspection[]> {
    return await db.inspections.where('status').equals(status).toArray();
  },

  /**
   * Create new inspection
   */
  async create(inspection: Inspection): Promise<string> {
    await db.inspections.add(inspection);
    return inspection.id;
  },

  /**
   * Update inspection
   */
  async update(id: string, updates: Partial<Inspection>): Promise<void> {
    await db.inspections.update(id, {
      ...updates,
      updated_at: new Date().toISOString(),
      locally_modified: true,
    });
  },

  /**
   * Create or update inspection
   */
  async upsert(inspection: Inspection): Promise<string> {
    await db.inspections.put(inspection);
    return inspection.id;
  },

  /**
   * Update inspection section
   */
  async updateSection(
    inspectionId: string,
    sectionId: string,
    updates: Partial<InspectionSection>
  ): Promise<void> {
    const inspection = await db.inspections.get(inspectionId);
    if (!inspection) throw new Error('Inspection not found');

    const sectionIndex = inspection.sections.findIndex(s => s.id === sectionId);
    if (sectionIndex === -1) throw new Error('Section not found');

    inspection.sections[sectionIndex] = {
      ...inspection.sections[sectionIndex]!,
      ...updates,
    };

    inspection.updated_at = new Date().toISOString();
    inspection.locally_modified = true;

    await db.inspections.put(inspection);
  },

  /**
   * Add photo to section
   */
  async addPhotoToSection(
    inspectionId: string,
    sectionId: string,
    photoId: string
  ): Promise<void> {
    const inspection = await db.inspections.get(inspectionId);
    if (!inspection) throw new Error('Inspection not found');

    const section = inspection.sections.find(s => s.id === sectionId);
    if (!section) throw new Error('Section not found');

    if (!section.photos.includes(photoId)) {
      section.photos.push(photoId);
      inspection.updated_at = new Date().toISOString();
      inspection.locally_modified = true;
      await db.inspections.put(inspection);
    }
  },

  /**
   * Add damage detection
   */
  async addDamageDetection(
    inspectionId: string,
    detection: DamageDetection
  ): Promise<void> {
    const inspection = await db.inspections.get(inspectionId);
    if (!inspection) throw new Error('Inspection not found');

    if (!inspection.damage_detections) {
      inspection.damage_detections = [];
    }

    inspection.damage_detections.push(detection);
    inspection.updated_at = new Date().toISOString();
    inspection.locally_modified = true;

    await db.inspections.put(inspection);
  },

  /**
   * Get all locally modified inspections
   */
  async getModified(): Promise<Inspection[]> {
    return await db.inspections.filter(i => i.locally_modified).toArray();
  },

  /**
   * Mark as synced
   */
  async markSynced(id: string): Promise<void> {
    await db.inspections.update(id, {
      locally_modified: false,
      synced_at: new Date().toISOString(),
    });
  },

  /**
   * Delete inspection
   */
  async delete(id: string): Promise<void> {
    // Also delete associated photos
    const photos = await photoOperations.getByInspection(id);
    await Promise.all(photos.map(p => photoOperations.delete(p.id)));
    await db.inspections.delete(id);
  },

  /**
   * Get completion percentage
   */
  async getCompletionPercentage(inspectionId: string): Promise<number> {
    const inspection = await db.inspections.get(inspectionId);
    if (!inspection || inspection.sections.length === 0) return 0;

    const completedCount = inspection.sections.filter(s => s.completed).length;
    return Math.round((completedCount / inspection.sections.length) * 100);
  },
};

// ============================================================================
// Enhanced CRUD Operations - Photos
// ============================================================================

export const photoOperations = {
  /**
   * Get photo by ID
   */
  async getById(id: string): Promise<Photo | undefined> {
    return await db.photos.get(id);
  },

  /**
   * Get all photos for an inspection
   */
  async getByInspection(inspectionId: string): Promise<Photo[]> {
    return await db.photos
      .where('inspection_id')
      .equals(inspectionId)
      .sortBy('captured_at');
  },

  /**
   * Get photos by section
   */
  async getBySection(inspectionId: string, sectionId: string): Promise<Photo[]> {
    return await db.photos
      .where(['inspection_id', 'section_id'])
      .equals([inspectionId, sectionId])
      .sortBy('captured_at');
  },

  /**
   * Get all unuploaded photos
   */
  async getUnuploaded(): Promise<Photo[]> {
    return await db.photos.filter(p => !p.uploaded).toArray();
  },

  /**
   * Get photos that need retry
   */
  async getNeedingRetry(maxAttempts: number = 3): Promise<Photo[]> {
    return await db.photos
      .filter(photo => !photo.uploaded && photo.upload_attempts < maxAttempts)
      .toArray();
  },

  /**
   * Create photo
   */
  async create(photo: Photo): Promise<string> {
    await db.photos.add(photo);
    return photo.id;
  },

  /**
   * Update photo
   */
  async update(id: string, updates: Partial<Photo>): Promise<void> {
    await db.photos.update(id, updates);
  },

  /**
   * Create or update photo
   */
  async upsert(photo: Photo): Promise<string> {
    await db.photos.put(photo);
    return photo.id;
  },

  /**
   * Bulk upsert photos
   */
  async bulkUpsert(photos: Photo[]): Promise<void> {
    await db.photos.bulkPut(photos);
  },

  /**
   * Mark photo as uploaded
   */
  async markUploaded(id: string, cloudPath: string): Promise<void> {
    await db.photos.update(id, {
      uploaded: true,
      uploaded_at: new Date().toISOString(),
      cloud_path: cloudPath,
    });
  },

  /**
   * Increment upload attempts
   */
  async incrementUploadAttempts(id: string): Promise<void> {
    const photo = await db.photos.get(id);
    if (photo) {
      await db.photos.update(id, {
        upload_attempts: photo.upload_attempts + 1,
      });
    }
  },

  /**
   * Delete photo
   */
  async delete(id: string): Promise<void> {
    await db.photos.delete(id);
  },

  /**
   * Get total size of all photos
   */
  async getTotalSize(): Promise<number> {
    const photos = await db.photos.toArray();
    return photos.reduce((sum, photo) => sum + photo.size, 0);
  },

  /**
   * Get count by inspection
   */
  async getCount(inspectionId?: string): Promise<number> {
    if (inspectionId) {
      return await db.photos.where('inspection_id').equals(inspectionId).count();
    }
    return await db.photos.count();
  },
};

// ============================================================================
// Enhanced CRUD Operations - Sync Queue
// ============================================================================

export const syncQueueOperations = {
  /**
   * Add item to sync queue
   */
  async add(item: Omit<SyncQueueItem, 'id' | 'created_at'>): Promise<string> {
    const queueItem: SyncQueueItem = {
      ...item,
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
    };

    await db.sync_queue.put(queueItem);
    return queueItem.id;
  },

  /**
   * Get all pending items sorted by priority
   */
  async getPending(): Promise<SyncQueueItem[]> {
    return await db.sync_queue
      .where('status')
      .equals('pending')
      .reverse()
      .sortBy('priority');
  },

  /**
   * Get failed items
   */
  async getFailed(): Promise<SyncQueueItem[]> {
    return await db.sync_queue.where('status').equals('failed').toArray();
  },

  /**
   * Get items by entity
   */
  async getByEntity(entityType: string, entityId: string): Promise<SyncQueueItem[]> {
    return await db.sync_queue
      .where(['entity_type', 'entity_id'])
      .equals([entityType, entityId])
      .toArray();
  },

  /**
   * Update item
   */
  async update(id: string, updates: Partial<SyncQueueItem>): Promise<void> {
    await db.sync_queue.update(id, updates);
  },

  /**
   * Update item status
   */
  async updateStatus(
    id: string,
    status: SyncQueueItem['status'],
    error?: string
  ): Promise<void> {
    await db.sync_queue.update(id, {
      status,
      ...(error !== undefined ? { error } : {}),
      last_attempt: new Date().toISOString(),
    });
  },

  /**
   * Increment retry count
   */
  async incrementRetry(id: string): Promise<void> {
    const item = await db.sync_queue.get(id);
    if (!item) return;

    const newRetryCount = item.retry_count + 1;
    const newStatus = newRetryCount >= item.max_retries ? 'failed' : item.status;

    await db.sync_queue.update(id, {
      retry_count: newRetryCount,
      status: newStatus,
      last_attempt: new Date().toISOString(),
    });
  },

  /**
   * Remove completed items
   */
  async cleanCompleted(): Promise<number> {
    const completed = await db.sync_queue.where('status').equals('completed').toArray();
    await db.sync_queue.bulkDelete(completed.map(item => item.id));
    return completed.length;
  },

  /**
   * Clear all queue items
   */
  async clear(): Promise<void> {
    await db.sync_queue.clear();
  },

  /**
   * Get queue count by status
   */
  async getCount(status?: SyncQueueItem['status']): Promise<number> {
    if (status) {
      return await db.sync_queue.where('status').equals(status).count();
    }
    return await db.sync_queue.count();
  },

  /**
   * Delete item
   */
  async delete(id: string): Promise<void> {
    await db.sync_queue.delete(id);
  },
};

// ============================================================================
// Enhanced CRUD Operations - Metadata
// ============================================================================

export const metadataOperations = {
  /**
   * Get metadata value
   */
  async get<T = unknown>(key: string): Promise<T | undefined> {
    const item = await db.metadata.get(key);
    return item?.value as T | undefined;
  },

  /**
   * Set metadata value
   */
  async set(key: string, value: unknown): Promise<void> {
    await db.metadata.put({
      key,
      value,
      updated_at: new Date().toISOString(),
    });
  },

  /**
   * Delete metadata
   */
  async delete(key: string): Promise<void> {
    await db.metadata.delete(key);
  },

  /**
   * Get last sync time
   */
  async getLastSync(): Promise<string | null> {
    return (await this.get<string>('last_sync')) ?? null;
  },

  /**
   * Update last sync time
   */
  async updateLastSync(): Promise<void> {
    await this.set('last_sync', new Date().toISOString());
  },

  /**
   * Get all metadata
   */
  async getAll(): Promise<AppMetadata[]> {
    return await db.metadata.toArray();
  },
};
